import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/i18n/format';
import { useGetNotesQuery, NoteFilter, NoteTypeEnum } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';

// Dummy notes for preview mode
const dummyNotes = [
  {
    id: 'n1',
    title: 'Deal Analysis',
    content: '## Deal Analysis\n\n### Momentum Indicators\n- Deal value increased from $80K to $120K after discovery\n- Stage progressed from Discovery → Proposal in 2 weeks\n- Champion actively selling internally\n\n### Risk Factors\n- **Procurement**: May require security review (2-3 week delay)\n- **Competition**: Incumbent vendor has auto-renewal clause',
    date: 'Feb 27, 2026',
    type: 'ai' as const,
  },
  {
    id: 'n2',
    title: 'Deal Notes',
    content: '## Deal Notes\n\n- Pricing approved by our VP Sales at 15% discount\n- They want annual billing, not monthly\n- Legal contact is Jessica Wu — very detail-oriented',
    date: 'Feb 20, 2026',
    type: 'manual' as const,
  },
];

interface UseDealNotesOptions {
  skip?: boolean;
}

/**
 * Hook for fetching notes associated with a deal
 * Supports Lovable Preview mode with dummy data
 */
export const useDealNotes = (dealId: string, options: UseDealNotesOptions = {}) => {
  const { skip = false } = options;
  const workspaceId = useWorkspaceId();
  // Kept in the memo deps below so a language switch re-renders the note dates.
  const { i18n } = useTranslation();
  const language = i18n.language;

  const filter: NoteFilter = useMemo(
    () => ({
      workspaceId: workspaceId || undefined,
      dealId: dealId,
      deleted: false,
    }),
    [workspaceId, dealId]
  );

  const { data, loading, error, refetch } = useGetNotesQuery({
    variables: {
      filter,
      first: 100,
    },
    skip: skip || isLovablePreview() || !workspaceId || !dealId,
    fetchPolicy: 'cache-and-network',
  });

  const notes = useMemo(() => {
    if (isLovablePreview()) {
      return dummyNotes;
    }

    return (
      data?.notes?.edges
        // Tasks are notes with type=task and render in the deal's separate Tasks
        // section — drop them here so a task doesn't appear in both places.
        // Filter client-side (only explicit tasks) rather than adding a
        // type=note server filter, which would also hide legacy notes whose
        // `type` isn't indexed.
        ?.filter((edge) => edge.node?.type !== NoteTypeEnum.Task)
        .map((edge) => ({
          id: edge.node?.id || '',
          title: edge.node?.title || 'Untitled Note',
          content: (edge.node as any).blocks || '',
          date: formatDate(edge.node?.createdAt, language),
          type: edge.node?.aiGenerated ? ('ai' as const) : ('manual' as const),
        })) || []
    );
  }, [data, language]);

  return {
    notes,
    loading: isLovablePreview() ? false : loading,
    error: isLovablePreview() ? null : error,
    refetch: isLovablePreview() ? (() => Promise.resolve({} as any)) : refetch,
  };
};
